// useGeocoding.js
import { useState, useCallback, useEffect } from "react";
import { batchGeocodeLocations } from "utils/geocode-util";

export const useGeocoding = () => {
  const [geocodeCache] = useState(() => new Map());
  const [progress, setProgress] = useState(0);

  const geocodeLocations = useCallback(
    async (locations) => {
      // Separate locations that already have coordinates from those that need geocoding
      const locationsWithCoords = [];
      const locationsNeedingGeocode = [];

      locations.forEach((loc) => {
        if (loc.lat && loc.lng) {
          // Already has precise coordinates, don't geocode
          locationsWithCoords.push(loc);
        } else if (loc.city && loc.state) {
          // Needs geocoding
          locationsNeedingGeocode.push(loc);
        }
      });

      // Only geocode locations that don't already have coordinates
      const uncachedLocations = locationsNeedingGeocode.filter(
        (loc) => !geocodeCache.has(`${loc.city}_${loc.state}`)
      );

      let geocodedResults = [];
      if (uncachedLocations.length > 0) {
        const geocoded = await batchGeocodeLocations(
          uncachedLocations,
          (completed, total) =>
            setProgress(Math.round((completed / total) * 100))
        );

        geocoded.forEach((loc) => {
          geocodeCache.set(`${loc.city}_${loc.state}`, loc.coordinates);
        });
        geocodedResults = geocoded;
      }

      // Combine locations with existing coordinates and newly geocoded locations
      const cachedResults = locationsNeedingGeocode
        .filter((loc) => geocodeCache.has(`${loc.city}_${loc.state}`))
        .map((loc) => {
          const coords = geocodeCache.get(`${loc.city}_${loc.state}`);
          return { ...loc, lat: coords[0], lng: coords[1] };
        });

      return [
        ...locationsWithCoords,
        ...geocodedResults,
        ...cachedResults,
      ].filter(Boolean);
    },
    [geocodeCache]
  );

  return { geocodeLocations, progress, geocodeCache };
};

export const useDealLocations = (
  consolidatedData,
  currentDealName,
  currentGroup
) => {
  const [selectedDeals, setSelectedDeals] = useState(new Set());
  const [visibleLocations, setVisibleLocations] = useState([]);
  const { geocodeLocations, progress } = useGeocoding();

  const processFacilities = useCallback(
    (dealName) => {
      if (!consolidatedData?.[dealName]?.facilities) return [];

      return Object.entries(consolidatedData[dealName].facilities)
        .filter(([name]) => name !== "Consolidated")
        .map(([name, facility]) => {
          const facilityData = {
            ...facility.details,
            Deal: dealName,
            Facilities: name,
          };

          // Check if facility already has precise coordinates (try both uppercase and lowercase)
          const coordinatesField =
            facility.details?.Coordinates || facility.details?.coordinates;

          if (coordinatesField) {
            try {
              let coordinates;
              if (typeof coordinatesField === "string") {
                coordinates = JSON.parse(coordinatesField);
              } else {
                coordinates = coordinatesField;
              }

              // Handle different coordinate formats
              if (Array.isArray(coordinates) && coordinates.length === 2) {
                facilityData.coordinates = coordinates;
                facilityData.lat = parseFloat(coordinates[0]);
                facilityData.lng = parseFloat(coordinates[1]);
              } else if (
                coordinates &&
                typeof coordinates === "object" &&
                coordinates.latitude !== undefined &&
                coordinates.longitude !== undefined
              ) {
                // Lowercase latitude/longitude
                facilityData.coordinates = [
                  parseFloat(coordinates.latitude),
                  parseFloat(coordinates.longitude),
                ];
                facilityData.lat = parseFloat(coordinates.latitude);
                facilityData.lng = parseFloat(coordinates.longitude);
              } else if (
                coordinates &&
                typeof coordinates === "object" &&
                coordinates.Latitude !== undefined &&
                coordinates.Longitude !== undefined
              ) {
                // Uppercase Latitude/Longitude
                facilityData.coordinates = [
                  parseFloat(coordinates.Latitude),
                  parseFloat(coordinates.Longitude),
                ];
                facilityData.lat = parseFloat(coordinates.Latitude);
                facilityData.lng = parseFloat(coordinates.Longitude);
              } else {
                console.log(
                  `[DEBUG] Unknown coordinate format for ${name}:`,
                  coordinates
                );
              }
            } catch (error) {
              console.warn(
                "Error parsing coordinates for facility:",
                name,
                error
              );
            }
          } else {
            console.log(
              `[DEBUG] No coordinates field found for facility ${name}. Available fields:`,
              Object.keys(facility.details || {})
            );
          }

          // If we don't have coordinates, prepare for geocoding
          if (!facilityData.lat || !facilityData.lng) {
            facilityData.city =
              facility.details?.["City/Town"] ||
              facility.details?.["City, State"]?.split(",")[0]?.trim();
            facilityData.state =
              facility.details?.State ||
              facility.details?.["City, State"]?.split(",")[1]?.trim();
          }

          return facilityData;
        })
        .filter((f) => (f.lat && f.lng) || (f.city && f.state)); // Include facilities with coordinates OR city/state
    },
    [consolidatedData]
  );

  const toggleDeal = useCallback(
    (dealName) => {
      if (dealName === currentDealName) return;

      setSelectedDeals((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(dealName)) {
          newSet.delete(dealName);
        } else {
          newSet.add(dealName);
        }
        return newSet;
      });
    },
    [currentDealName]
  );

  useEffect(() => {
    // When a deal is selected, show it on the map
    if (currentDealName) {
      setSelectedDeals(new Set([currentDealName]));
    } else {
      setSelectedDeals(new Set());
    }
  }, [currentDealName]);

  useEffect(() => {
    const updateLocations = async () => {
      let allFacilities = [];

      const dealsToShow = new Set([currentDealName, ...selectedDeals]);

      if (currentGroup && currentGroup.facilityIds?.length > 0) {
        // Group is selected: show all visible deals. For the group's deal/portfolio,
        // show all facilities but tag non-members so they can render smaller.
        allFacilities = Array.from(dealsToShow)
          .filter(Boolean)
          .flatMap((dealName) => {
            if (dealName === currentGroup.dealId) {
              return processFacilities(dealName).map((f) => ({
                ...f,
                _isNonGroupFacility: !currentGroup.facilityIds.includes(f.Facilities),
              }));
            }
            // All other deals/portfolios show normally
            return processFacilities(dealName);
          });
      } else {
        // No group selected — show all visible deals normally
        allFacilities = Array.from(dealsToShow)
          .filter(Boolean)
          .flatMap(processFacilities);
      }

      const geocodedFacilities = await geocodeLocations(allFacilities);
      setVisibleLocations(geocodedFacilities);
    };

    updateLocations();
  }, [
    currentDealName,
    selectedDeals,
    processFacilities,
    geocodeLocations,
    currentGroup,
    consolidatedData,
  ]);

  return {
    selectedDeals,
    visibleLocations,
    toggleDeal,
    progress,
  };
};
